import axios, {
  type AxiosDefaults,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosHeaders,
} from 'axios';
import { urlTemplateInterceptor } from './interceptors/url-template.interceptor';
import { bearerTokenInterceptor } from './interceptors/bearer-token.interceptor';
import qs from 'qs';
import { apimKeyInterceptor } from './interceptors/apim-key.interceptor';
import { acceptLanguageInterceptor } from './interceptors/accept-language.interceptor';
import { authService, AuthService } from '../auth/auth.service';
import { AUTH_TOKEN_URL } from '../auth/auth-constants';
import type { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh';
import { AuthStorageService } from '../auth/auth-storage.service';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { contentfulBearerTokenInterceptor } from './interceptors/contentful-bearer-token.interceptor';
import { Logger } from '../../feature/logger/logger';

type CustomHttpServiceConfig = {
  allowListBearer?: (string | RegExp)[];
  disallowListBearer?: (string | RegExp)[];
  pathParams?: Record<string, string | number | boolean>;
};

export type ExtendedAxiosDefaults<T> = AxiosDefaults<T> & CustomHttpServiceConfig;

export type HttpServiceRequestConfig = AxiosRequestConfig & CustomHttpServiceConfig;

export type InternalHttpServiceRequestHeaders = InternalAxiosRequestConfig & CustomHttpServiceConfig;

export class HttpService {
  private _axiosInstance: AxiosInstance;
  private static readonly _useWarpAuth: boolean = process.env.NEXT_PUBLIC_WARP_AUTH_ENABLED === 'true';

  constructor(baseUrl?: string, withCredentials = true) {
    this._axiosInstance = axios.create({
      baseURL: baseUrl || process.env.NEXT_PUBLIC_BASE_URL || '',
      withCredentials,
      // Reconfigure the params serializer to concat arrays with commas
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'comma' }),
      allowListBearer: process.env.NEXT_PUBLIC_ALLOW_LIST_BEARER ? process.env.NEXT_PUBLIC_ALLOW_LIST_BEARER.split(' ') : [],
      disallowListBearer: process.env.NEXT_PUBLIC_DISALLOW_LIST_BEARER ? process.env.NEXT_PUBLIC_DISALLOW_LIST_BEARER.split(' ') : [],
      headers: {
        'Content-Type': 'application/json',
      },
    } as HttpServiceRequestConfig);

    this.setupInterceptors();
  }

  private readonly refreshAuthLogic = async (failedRequest: any) => {
    if (!AuthStorageService.getRefreshToken()) {
      return Promise.reject(failedRequest);
    }

    try {
      const data = AuthService.getRefreshTokenBody();
      const response = await this._axiosInstance.post(AUTH_TOKEN_URL, qs.stringify(data), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;',
        },
        skipAuthRefresh: true,
      } as AxiosAuthRefreshRequestConfig);

      if (response.status === 200) {
        AuthStorageService.clearPkceFlowData();
        AuthStorageService.saveAuthResponse(response.data);

        failedRequest.response.config.headers['Authorization'] = `Bearer ${AuthStorageService.getAccessToken()}`;
        return Promise.resolve();
      }

      return Promise.reject(response);
    } catch (e) {
      // invalid grant
      AuthStorageService.clearSession();
      AuthStorageService.clearPkceFlowData();
      AuthService.logIn();
      return Promise.reject(e);
    }
  };

  private readonly warpAuthResponseInterceptor = async (error: AxiosError) => {
    // Only handle 401 errors
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Don't handle 401s from the status endpoint
    if (error.config?.url?.endsWith('/status')) {
      return Promise.reject(error);
    }

    // If the user is logging out, we don't need to check the token validity
    const userIsLoggingOut = authService.getState() === 'logging-out';
    if (userIsLoggingOut) {
      return Promise.resolve();
    }

    Logger.debug('(HttpService)', 'Received 401, checking token validity');
    const idToken = AuthStorageService.getIdToken();
    if (!idToken) {
      Logger.debug('(HttpService)', 'No ID token found, redirecting to login');
      AuthService.logIn();
      return Promise.reject(error);
    }

    try {
      const isValid = await AuthService.hasValidToken();
      if (!isValid) {
        Logger.debug('(HttpService)', 'Token invalid, redirecting to login');
        AuthService.logIn();
      }
    } catch (validationError) {
      Logger.debug('(HttpService)', 'Error validating token, redirecting to login');
      AuthService.logIn();
    }

    return Promise.reject(error);
  };

  private setupInterceptors() {
    // Contentful bearer token interceptor
    this._axiosInstance.interceptors.request.use(contentfulBearerTokenInterceptor);

    // Interceptor to handle url templates according to RFC 6570
    this._axiosInstance.interceptors.request.use(urlTemplateInterceptor);

    // Interceptor to always submit the correct locale based on the URL
    this._axiosInstance.interceptors.request.use(acceptLanguageInterceptor);

    if (HttpService._useWarpAuth) {
      this._axiosInstance.interceptors.request.use(this.warpAuthInterceptor);
      this._axiosInstance.interceptors.response.use(
        (response) => response, // Success handler
        this.warpAuthResponseInterceptor // Error handler
      );
    } else {
      // Interceptor to add apim subscription key to all relevant requests
      this._axiosInstance.interceptors.request.use(apimKeyInterceptor);

      // Interceptor to handle appending the bearer token
      this._axiosInstance.interceptors.request.use(bearerTokenInterceptor);

      // Interceptor to refresh the auth token
      createAuthRefreshInterceptor(this._axiosInstance, this.refreshAuthLogic);
    }
  }

  private readonly warpAuthInterceptor = (config: InternalHttpServiceRequestHeaders) => {
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    if (config.baseURL?.startsWith(process.env.NEXT_PUBLIC_WARP_BASE_URL as string)) {
      config.headers['x-client-id'] = process.env.NEXT_PUBLIC_AUTH_CLIENT_ID;
    }

    return config;
  };

  getAxiosInstance = () => {
    return this._axiosInstance;
  };

  addRequestInterceptor = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFulfilled?: (value: InternalAxiosRequestConfig<any>) => InternalAxiosRequestConfig<any> | Promise<InternalAxiosRequestConfig<any>>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onRejected?: (error: AxiosError<any, any>) => any
  ) => {
    this._axiosInstance.interceptors.request.use(onFulfilled, onRejected);
  };

  addResponseInterceptor = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFulfilled?: <T>(value: AxiosResponse<T, any>) => AxiosResponse<T, any> | AxiosResponse<any, any> | Promise<AxiosResponse<any, any>>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onRejected?: (error: AxiosError<any, any>) => any
  ) => {
    this._axiosInstance.interceptors.response.use(onFulfilled, onRejected);
  };

  setAllowListBearer = (allowList: (string | RegExp)[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this._axiosInstance.defaults as ExtendedAxiosDefaults<any>).allowListBearer = allowList;
  };

  setDisallowListBearer = (disallowList: (string | RegExp)[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this._axiosInstance.defaults as ExtendedAxiosDefaults<any>).disallowListBearer = disallowList;
  };

  getAllowListBearer = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this._axiosInstance.defaults as ExtendedAxiosDefaults<any>).allowListBearer;
  };

  getDisallowListBearer = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this._axiosInstance.defaults as ExtendedAxiosDefaults<any>).disallowListBearer;
  };

  async get<R>(
    path: string,
    options?: HttpServiceRequestConfig,
    extractResponseHeader?: (response: AxiosResponse<R>, responseBody: R) => R
  ): Promise<R> {
    return this.sendRequest<R>(
      path,
      {
        ...options,
        method: 'GET',
      },
      extractResponseHeader
    );
  }

  async post<R, B>(path: string, body?: B, options?: HttpServiceRequestConfig): Promise<R> {
    return this.sendRequest<R>(path, {
      ...options,
      data: body,
      method: 'POST',
      pathParams: options?.pathParams,
      params: options?.params,
    });
  }

  async put<R, B>(path: string, body?: B, options?: HttpServiceRequestConfig): Promise<R> {
    return this.sendRequest<R>(path, {
      ...options,
      data: body,
      method: 'PUT',
    });
  }

  async patch<R, B>(path: string, body?: B, options?: HttpServiceRequestConfig): Promise<R> {
    return this.sendRequest<R>(path, {
      ...options,
      data: body,
      method: 'PATCH',
    });
  }

  async delete<R, B>(path: string, body?: B, options?: HttpServiceRequestConfig): Promise<R> {
    return this.sendRequest<R>(path, {
      ...options,
      data: body,
      method: 'DELETE',
    });
  }

  private async sendRequest<T>(
    url: string,
    requestConfig: Partial<HttpServiceRequestConfig>,
    extractResponseHeader?: (response: AxiosResponse<T>, responseBody: T) => T
  ): Promise<T> {
    const response = await this._axiosInstance({
      ...requestConfig,
      url,
    });

    if (extractResponseHeader) {
      return extractResponseHeader(response, response.data);
    }

    return response.data;
  }
}
